import { API_BASE_URL } from "../api";
import { readEvents } from "./chat";

export const SALES_SANDBOX_URL = `${API_BASE_URL}/core/sales/sandbox/`;
export const SALES_SLOTS_URL = `${API_BASE_URL}/core/sales/sandbox/slots/`;
export const SALES_MAX_CHARS = 12000;

export const SAMPLE_COMPANY = "BrightSite Web Studio";
export const SAMPLE_OFFER = `# Packages
Starter website: 5 pages, mobile friendly, contact form. Price: 900 dollars. Ready in 2 weeks.
Business website: up to 12 pages, blog, SEO setup, WhatsApp chat button. Price: 2,400 dollars. Ready in 4 weeks.
Online shop: product catalogue, cart, card payments, order emails. Price from 4,500 dollars. Ready in 6 to 8 weeks.

# Care plans
Monthly care plan: hosting, backups, security updates and 2 hours of changes. 79 dollars per month.

# How we work
We start with a free 30 minute call. You get a written quote within 2 working days. We take 40 percent upfront and 60 percent on launch.
`;
export const SAMPLE_QUESTIONS = ["How many pages do you think you need?"];
export const GOALS = [["leads", "Collect leads"], ["meetings", "Book meetings"], ["sales", "Help visitors buy"]];
export const TRY_MESSAGES = [
  "I need a website for my bakery with online ordering",
  "How much is the online shop and how long does it take?",
  "My name is Omar Farooq from Green Leaf Bakery, budget around 5k, we need it within 2 months. omar@greenleaf.com",
  "Can we book a call?",
];

export const GRADE_LABELS = { cold: "Cold", warm: "Warm", hot: "Hot" };
export const GRADE_STYLES = { cold: "bg-sky-500/20 text-sky-200 border-sky-400/40", warm: "bg-amber-500/20 text-amber-200 border-amber-400/40", hot: "bg-red-500/25 text-red-200 border-red-400/50" };
export const FIELD_LABELS = [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"], ["need", "Needs"], ["budget", "Budget"], ["timeline", "Timeline"]];

// "# Title" sections of the offer text.
export function offerStats(text) {
  const value = String(text || "");
  return { chars: value.length, sections: value.split("\n").filter((line) => /^\s*#{1,3}\s*\S/.test(line)).length, over: value.length > SALES_MAX_CHARS };
}

// The fields of a lead card that are filled in, in the order they are shown (the bot's own answers last).
export function leadRows(fields) {
  const f = fields || {};
  const rows = FIELD_LABELS.filter(([key]) => f[key]).map(([key, label]) => ({ key, label, value: f[key] }));
  const labels = f.answer_labels || {};
  Object.entries(f.answers || {}).forEach(([key, value]) => { if (value) rows.push({ key, label: labels[key] || key.replace(/_/g, " "), value }); });
  return rows;
}

// How complete the lead is: how many of the useful details are known.
export const completeness = (fields) => {
  const f = fields || {};
  const keys = ["name", "email", "phone", "company", "need", "budget", "timeline"];
  return Math.round((100 * keys.filter((k) => f[k]).length) / keys.length);
};

// Free times grouped by day: [{ date, label, times: [{ start, time, label }] }]
export function groupSlots(slots) {
  const days = [];
  (slots || []).forEach((slot) => {
    let day = days.find((d) => d.date === slot.date);
    if (!day) { day = { date: slot.date, label: String(slot.label || "").split(",")[0], times: [] }; days.push(day); }
    day.times.push({ start: slot.start, time: slot.time, label: slot.label });
  });
  return days;
}

// What the team would get for this lead: the e-mail and the webhook (shown in the try-it box so the visitor sees the result).
export function leadAlert(company, lead) {
  const f = lead.fields || {};
  const reasons = (lead.reasons || []).map((r) => `  ${r.points > 0 ? "+" : ""}${r.points}  ${r.why}`);
  const email = [
    `Subject: [${company || "Sales Bot"}] ${lead.grade === "hot" ? "HOT lead" : "New lead"}: ${f.name || f.email || f.phone || "visitor"}`, "",
    `Name: ${f.name || "-"}`, `Email: ${f.email || "-"}`, `Phone: ${f.phone || "-"}`, `Company: ${f.company || "-"}`, `Needs: ${f.need || "-"}`, `Budget: ${f.budget || "-"}`, `Timeline: ${f.timeline || "-"}`,
    `Score: ${lead.score}/100 (${lead.grade})`, "", "Why this score:", ...reasons,
  ].join("\n");
  const webhook = { event: lead.grade === "hot" ? "lead.hot" : "lead.created", chatbot: { name: company || "Sales Bot" }, lead: { ...f, score: lead.score, grade: lead.grade, status: "new" } };
  return { email, webhook: JSON.stringify(webhook, null, 2) };
}

// Asks the try-it box. onEvent gets { start }, { delta }, { done, reply, lead, ask_contact, show_meeting, learned }, { error: { message } }.
export async function askSalesSandbox({ company, knowledge, goal, questions, message, history, learned, onEvent, signal }) {
  let response;
  try {
    response = await fetch(SALES_SANDBOX_URL, { method: "POST", signal, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ company, knowledge, goal, questions, message, history, learned }) });
  } catch (err) {
    if (err && err.name === "AbortError") throw err;
    onEvent({ error: { message: "Could not reach the server. Check your connection and try again." } });
    return;
  }
  if (!response.ok || !(response.headers.get("content-type") || "").includes("event-stream")) {
    let text = "Sorry, something went wrong. Please try again.";
    try { text = (await response.json()).error.message || text; } catch { /* keep the general message */ }
    onEvent({ error: { message: text } });
    return;
  }
  await readEvents(response, onEvent);
}

export async function fetchSandboxSlots(tz) {
  try {
    const response = await fetch(`${SALES_SLOTS_URL}${tz ? `?tz=${encodeURIComponent(tz)}` : ""}`);
    return response.ok ? await response.json() : { slots: [], timezone: "UTC", minutes: 30 };
  } catch {
    return { slots: [], timezone: "UTC", minutes: 30 };
  }
}

// Ready-to-copy examples for a client's developer.
export function salesSamples(base, key = "YOUR_API_KEY", widgetKey = "YOUR_WIDGET_KEY") {
  const api = `${base}/core/chatbot/v1`;
  return {
    leads: `# 1. The visitor chats. The answer says what the bot learned, and what to show next.
curl -X POST ${api}/chat/ \\
  -H "Authorization: Bearer ${key}" -H "Content-Type: application/json" \\
  -d '{"message": "We need a shop for our bakery, budget about 5k, within 2 months. I am Omar, omar@greenleaf.com",
       "context": {"page_url": "https://yoursite.com/pricing", "utm": {"utm_source": "google"}}}'
# -> { "reply": "...", "lead": { "captured": true, "reference": "L-00012", "score": 80, "grade": "hot", "reasons": [...] },
#      "ask_contact": false, "show_meeting": false, "conversation_id": "cv_..." }

# 2. A visitor fills your own form (name, email or phone, consent)
curl -X POST ${api}/leads/ \\
  -H "Authorization: Bearer ${key}" -H "Content-Type: application/json" \\
  -d '{"name": "Sara Khan", "email": "sara@example.com", "company": "Khan Co", "message": "Quote for a mobile app", "consent": true}'

# 3. Your CRM reads the hot leads, updates one, or erases one (privacy request)
curl "${api}/leads/?grade=hot&status=new" -H "Authorization: Bearer ${key}"
curl -X PATCH ${api}/leads/12/ -H "Authorization: Bearer ${key}" -H "Content-Type: application/json" -d '{"status": "contacted", "assigned_to": "Ali"}'
curl -X DELETE ${api}/leads/12/ -H "Authorization: Bearer ${key}"`,
    meetings: `# Free times of your sales team
curl ${api}/meetings/slots/ -H "Authorization: Bearer ${key}"
# -> { "timezone": "Asia/Karachi", "minutes": 30, "slots": [ { "start": "2026-11-05T04:00:00Z", "label": "Thu 05 Nov, 09:00", ... } ] }

# Book one (returns the meeting and a calendar file in "ics")
curl -X POST ${api}/meetings/ \\
  -H "Authorization: Bearer ${key}" -H "Content-Type: application/json" \\
  -d '{"start": "2026-11-05T04:00:00Z", "name": "Sara Khan", "email": "sara@example.com", "consent": true}'

# Cancel
curl -X DELETE ${api}/meetings/7/ -H "Authorization: Bearer ${key}"`,
    webhook: `// Your server receives a POST for: lead.created, lead.hot, lead.updated, meeting.booked, meeting.cancelled
// Header X-Signature: sha256=<HMAC-SHA256 of the raw body with your webhook secret>

// Node.js (Express): check the signature before trusting the body
const crypto = require("crypto");
app.post("/hooks/leads", express.raw({ type: "application/json" }), (req, res) => {
  const expected = "sha256=" + crypto.createHmac("sha256", process.env.WEBHOOK_SECRET).update(req.body).digest("hex");
  const sent = req.get("X-Signature") || "";
  if (sent.length !== expected.length || !crypto.timingSafeEqual(Buffer.from(sent), Buffer.from(expected))) return res.sendStatus(401);
  const { event, lead } = JSON.parse(req.body);
  // lead: { reference, name, email, phone, company, need, budget, timeline, score, grade, source_page, utm, ... }
  res.sendStatus(200);
});

# Python (Flask)
import hmac, hashlib
expected = "sha256=" + hmac.new(SECRET.encode(), request.get_data(), hashlib.sha256).hexdigest()
if not hmac.compare_digest(expected, request.headers.get("X-Signature", "")):
    abort(401)`,
    embed: `<!-- One line before </body>. The bot greets visitors, qualifies them, saves the lead and offers a calendar. -->
<script src="${base}/core/chatbot/widget.js" data-key="${widgetKey}" async></script>`,
  };
}
